import { useState } from 'react'
import { cn } from '@/lib/cn'

/**
 * Marketplace domains — pulse v1's `CHANNEL_DOMAIN` map in
 * `oms-po-ingest/pulse/src/components/ui.tsx`. Vision's `ChannelLogos`
 * emits the same Google-favicon URLs; we keep the map as a fallback for
 * older Vision builds that omit `logoUrl`.
 */
const CHANNEL_DOMAIN: Record<string, string> = {
  amazon: 'amazon.in',
  amazon_now: 'amazon.in',
  blinkit: 'blinkit.com',
  zepto: 'zeptonow.com',
  flipkart: 'flipkart.com',
  flipkart_minutes: 'flipkart.com',
  myntra: 'myntra.com',
  ajio: 'ajio.com',
  bbnow: 'bigbasket.com',
  swiggy: 'swiggy.com',
  instamart: 'swiggy.com',
  bigbasket: 'bigbasket.com',
}

export function v1ChannelLogoUrl(channel: string | null | undefined): string | undefined {
  if (!channel) return undefined
  const domain = CHANNEL_DOMAIN[channel.toLowerCase().replace(/-/g, '_')]
  if (!domain) return undefined
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`
}

/**
 * Channel logo tile. Prefers Vision's `logoUrl` (the v1 Google-favicon
 * URL) and falls back to the same URL derived from the marketplace
 * family. Letter avatar only when both are missing or the img errors
 * (CSP still blocking google.com until the CloudFront header hotfix
 * rolls).
 */
export function ChannelCell({
  channel,
  logoUrl,
  size = 14,
  className,
}: {
  channel: string
  logoUrl?: string | null
  size?: number
  className?: string
}) {
  const [failed, setFailed] = useState(false)
  const src = logoUrl || v1ChannelLogoUrl(channel)
  const initial = (channel?.trim()?.charAt(0) || '?').toUpperCase()

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className={cn('shrink-0 rounded-[3px] object-contain', className)}
        style={{ width: size, height: size }}
      />
    )
  }

  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-[3px] bg-[color:var(--muted)] font-bold text-[color:var(--muted-foreground)]',
        className,
      )}
      style={{ width: size, height: size, fontSize: Math.max(8, size * 0.58) }}
    >
      {initial}
    </span>
  )
}
